import React from 'react';
import i18n from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import cn from './locales/zh-CN.js';
import vi from './locales/vi.js';
import th from './locales/th.js';
import en from './locales/en.js';
import bn from './locales/bn.js';
import hi from './locales/hi.js';
import ja from './locales/ja.js';
import ko from './locales/ko.js';
import pt from './locales/pt.js';
import ru from './locales/ru.js';
import tw from './locales/zh-TW.js';

import { LanguageCodeEnums, TranslatableLanguageCodes } from '../enums.js';

const {
	ZH_CN,
	VI,
	TH,
	EN,
	BN,
	HI,
	JA,
	KO,
	PT,
	RU,
	ZH_TW,
} = LanguageCodeEnums;
const LanguageSourceMap = {
	[ZH_CN]: {
		translation: cn,
	},
	[VI]: {
		translation: vi,
	},
	[TH]: {
		translation: th,
	},
	[EN]: {
		translation: en,
	},
	[BN]: {
		translation: bn,
	},
	[HI]: {
		translation: hi,
	},
	[JA]: {
		translation: ja,
	},
	[KO]: {
		translation: ko,
	},
	[PT]: {
		translation: pt,
	},
	[RU]: {
		translation: ru,
	},
	[ZH_TW]: {
		translation: tw,
	},
};
const DEFAULT_LOCALE = EN;
const i18nLanguageCodeMap = {
	'zh-cn': ZH_CN,
	'zh-CN': ZH_CN,
	zh: ZH_CN,
	zhCN: ZH_CN,
	vi: VI,
	th: TH,
	en: EN,
	bn: BN,
	hi: HI,
	ja: JA,
	ko: KO,
	pt: PT,
	ru: RU,
	'zh-tw': ZH_TW,
};
const StyleLanguageClassNameEnums = {
	[ZH_CN]: `cs2-${ZH_CN}`,
	[VI]: `cs2-${VI}`,
	[TH]: `cs2-${TH}`,
	[EN]: `cs2-${EN}`,
	[BN]: `cs2-${BN}`,
	[HI]: `cs2-${HI}`,
	[JA]: `cs2-${JA}`,
	[KO]: `cs2-${KO}`,
	[PT]: `cs2-${PT}`,
	[RU]: `cs2-${RU}`,
	[ZH_TW]: `cs2-${ZH_TW}`,
};

function initI18n(locale = DEFAULT_LOCALE) {
	const languageCode = i18nLanguageCodeMap[locale];

	return i18n
		.use(initReactI18next)
		.init({
			resources: LanguageSourceMap,
			fallbackLng: DEFAULT_LOCALE,
			lng: languageCode,
			lowerCaseLng: true,
		}, () => {
			changeLanguage(languageCode);
		});
}

function changeStyleLanguage(languageCode) {
	document.querySelector('body').className = StyleLanguageClassNameEnums[languageCode];
}

function changei18nLanguage(languageCode) {
	return i18n.changeLanguage(languageCode);
}

function changeLanguage(locale) {
	const languageCode = i18nLanguageCodeMap[locale];

	changeStyleLanguage(languageCode);
	changei18nLanguage(languageCode);
}

function getTranslation(word, options) {
	return i18n.t(word, options);
}

/**
Example:
t(
	'每過 <<counter>> 分鐘, 這世界就流失 <<counter2>> 秒鐘',
	{},
	{
		counter: <span>1</span>,
		counter2: <span>60</span>,
	},
)
*/

function useCustomTranslation() {
	const { t, ...args } = useTranslation();
	const tWithCustomComponent = (key, interpolation, components) => {
		const translatedText = t(key, interpolation);

		if (components) {
			const parts = translatedText.split(/(<<\w+>>)/).map(part => {
				const match = part.match(/<<(\w+)>>/);

				if (match) {
					const componentKey = match[1];

					return <span key={componentKey}>{components[componentKey]}</span> || match[0];
				}

				return part;
			});

			return <>{parts}</>;
		}

		return translatedText;
	};

	return { t: tWithCustomComponent, ...args };
}

const SystemTextEnum = {
	CONTACT_CS: '联络人工客服',
	FORM_PLACEHOLDER: '请输入',
};
// 前端 locales 認不出的 DB 既存值:對齊 data.csv 前的舊譯文,管理端當年以該語系寫進 title / placeHolder
const SystemTextExtraMap = {
	[SystemTextEnum.CONTACT_CS]: [
		// 亦為本地泰語平台 seed 值,見 server/fixtures/data/testing-qa-node.js
		'ติดต่อฝ่ายบริการลูกค้าของมนุษย์',
		'Falar com atendente',
	],
	[SystemTextEnum.FORM_PLACEHOLDER]: [
		'कृपया प्रवेश करें',
		'입력하세요',
		'Insira',
		'Введите',
	],
};

// 命中者視為系統字串:顯示改用當前語系譯文,CONTACT_CS 另走轉接人工客服而不進該 QA 節點
// CONTACT_CS 改以 qaOptions.options[].action 判斷,此處僅供舊 reply fallback
function getIsSystemText(systemText, text) {
	if (!text) {
		return false;
	}

	return (SystemTextExtraMap[systemText] || []).includes(text) || TranslatableLanguageCodes.some(lng => i18n.t(systemText, { lng }) === text);
}

export {
	initI18n,
	changeLanguage,
	getTranslation,
	useCustomTranslation,
	SystemTextEnum,
	getIsSystemText,
};
